<template>
  <div class="robot-quick-education">
    <el-breadcrumb separator-class="el-icon-arrow-right">
      <el-breadcrumb-item :to="{ path: '/menu/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>内容管理</el-breadcrumb-item>
      <el-breadcrumb-item>机器人快捷宣教</el-breadcrumb-item>
    </el-breadcrumb>

    <el-form inline class="toolbar">
      <el-form-item label="医院">
        <el-select v-model="hospitalName" filterable placeholder="选择医院" @change="onHospitalChange">
          <el-option v-for="item in hospitals" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="科室">
        <el-select v-model="departmentName" filterable placeholder="选择科室" :disabled="!hospitalName" @change="loadConfig">
          <el-option v-for="item in departments" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" icon="el-icon-plus" :disabled="!departmentName" @click="openDirectoryDialog">新增目录</el-button>
      </el-form-item>
    </el-form>

    <el-alert v-if="departmentName" :title="hospitalName + ' / ' + departmentName" type="info" :closable="false" />
    <el-card v-if="departmentName" shadow="never" class="source-card">
      <div slot="header" class="panel-header">
        <span>机器人端宣教内容来源</span>
      </div>
      <div class="source-switch">
        <el-switch
          v-model="useOtherDepartment"
          :disabled="savingScope"
          active-text="使用其他医院科室"
          inactive-text="使用本科室配置"
          @change="onUseOtherChange" />
      </div>
      <div class="source-body">
        <el-select v-model="sourceHospitalName" filterable placeholder="选择内容来源医院" :disabled="savingScope" @change="onSourceHospitalChange">
          <el-option v-for="item in hospitals" :key="item.value" :label="item.label" :value="item.value" />
        </el-select>
        <el-select v-model="sourceDepartmentName" filterable placeholder="选择内容来源科室" :disabled="savingScope || !sourceHospitalName" @change="onSourceDepartmentChange">
          <el-option
            v-for="item in sourceDepartments"
            :key="item.value"
            :label="item.label"
            :value="item.value"
            :disabled="sourceHospitalName === hospitalName && item.value === departmentName" />
        </el-select>
        <el-button type="primary" :loading="savingScope" :disabled="!sourceDepartmentName" @click="saveContentScope()">保存来源</el-button>
        <el-button v-if="useOtherDepartment" :disabled="savingScope" @click="onUseOtherChange(false)">取消来源</el-button>
      </div>
      <div class="source-tip">
        开启后，<b>{{ hospitalName }} / {{ departmentName }}</b> 的机器人在快捷宣教中读取的目录与文章，直接使用所选医院科室已配置的内容（内容需在来源科室维护）。选择来源科室后自动生效，点击“取消来源”或关闭开关即恢复使用本科室配置。
      </div>
    </el-card>
    <el-alert
      v-if="departmentName && useOtherDepartment && sourceHospitalName"
      :title="'机器人端当前使用的宣教内容来自：' + sourceHospitalName + ' / ' + sourceDepartmentName"
      type="warning"
      :closable="false"
      class="source-alert">
      <a class="source-link" @click="goToSourceDepartment">前往来源科室维护宣教内容</a>
    </el-alert>
    <el-row v-if="departmentName" :gutter="16" class="workspace">
      <el-col :span="7">
        <el-card shadow="never">
          <div slot="header" class="panel-header"><span>快捷宣教目录</span></div>
          <el-menu :default-active="selectedDirectoryId" @select="selectDirectory">
            <el-menu-item v-for="item in directories" :key="item.id" :index="String(item.id)">
              <span>{{ item.name }}</span>
              <span class="directory-actions">
                <i class="el-icon-edit" @click.stop="openDirectoryDialog(item)" />
                <i class="el-icon-delete" @click.stop="deleteDirectory(item)" />
              </span>
            </el-menu-item>
          </el-menu>
          <el-empty v-if="!directories.length" description="暂无目录" />
        </el-card>
      </el-col>
      <el-col :span="17">
        <el-card shadow="never">
          <div slot="header" class="panel-header">
            <span>{{ selectedDirectory ? selectedDirectory.name : '选择左侧目录' }}</span>
            <el-button v-if="selectedDirectory" type="primary" size="mini" icon="el-icon-plus" @click="articleDialogVisible = true">添加文章</el-button>
          </div>
          <el-table v-if="selectedDirectory" :data="selectedDirectory.articles || []" border stripe>
            <el-table-column label="编号" width="76"><template slot-scope="scope">{{ scope.row.displayCode || '—' }}</template></el-table-column>
            <el-table-column prop="title" label="文章标题" min-width="220" />
            <el-table-column label="类型" width="100">
              <template slot-scope="scope">{{ Number(scope.row.contentType) === 2 ? '视频' : '图文' }}</template>
            </el-table-column>
            <el-table-column label="可见范围" min-width="220">
              <template slot-scope="scope">{{ scope.row.scopeText || '全局可见' }}</template>
            </el-table-column>
            <el-table-column label="科室可见" width="120">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.visibleToDepartment === false" type="danger" size="mini">科室不可见</el-tag>
                <el-tag v-else-if="scope.row.visibleToDepartment === true" type="success" size="mini">可见</el-tag>
                <span v-else>—</span>
              </template>
            </el-table-column>
            <el-table-column label="操作" width="90">
              <template slot-scope="scope"><el-button type="text" @click="removeArticle(scope.row)">移除</el-button></template>
            </el-table-column>
          </el-table>
          <el-empty v-else description="请选择目录" />
        </el-card>
      </el-col>
    </el-row>

    <el-dialog title="选择科普宣教文章" :visible.sync="articleDialogVisible" width="900px" :close-on-click-modal="false" @open="loadArticleCategories">
      <div class="article-picker">
        <el-menu class="category-list" :default-active="selectedCategoryId" @select="selectCategory">
          <el-menu-item v-for="item in categories" :key="item.id" :index="String(item.id)">{{ item.firstName }} / {{ item.secName }}</el-menu-item>
        </el-menu>
        <div class="article-results">
          <el-input v-model="articleKeyword" clearable placeholder="全局搜索文章标题" class="keyword" @input="searchArticles" />
          <el-table ref="articleTable" :data="filteredArticles" height="420" @selection-change="onArticleSelection">
            <el-table-column type="selection" width="50" :selectable="isArticleSelectable" />
            <el-table-column prop="title" label="文章标题" min-width="240" />
            <el-table-column label="类型" width="80"><template slot-scope="scope">{{ Number(scope.row.contentType) === 2 ? '视频' : '图文' }}</template></el-table-column>
            <el-table-column label="可见范围" min-width="200"><template slot-scope="scope">{{ scope.row.scopeText || '全局可见' }}</template></el-table-column>
            <el-table-column label="科室可见" width="120">
              <template slot-scope="scope">
                <el-tag v-if="scope.row.visibleToDepartment === false" type="danger" size="mini">科室不可见</el-tag>
                <el-tag v-else-if="scope.row.visibleToDepartment === true" type="success" size="mini">可见</el-tag>
                <span v-else>—</span>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
      <span slot="footer"><el-button @click="articleDialogVisible = false">取消</el-button><el-button type="primary" :loading="savingArticles" @click="saveArticles">保存选择</el-button></span>
    </el-dialog>

    <el-dialog :title="directoryForm.id ? '编辑目录' : '新增目录'" :visible.sync="directoryDialogVisible" width="420px">
      <el-form ref="directoryForm" :model="directoryForm" :rules="directoryRules" label-width="80px">
        <el-form-item label="目录名称" prop="name"><el-input v-model="directoryForm.name" maxlength="50" show-word-limit /></el-form-item>
        <el-form-item label="排序"><el-input-number v-model="directoryForm.sortOrder" :min="0" :max="9999" /></el-form-item>
      </el-form>
      <span slot="footer"><el-button @click="directoryDialogVisible = false">取消</el-button><el-button type="primary" @click="saveDirectory">保存</el-button></span>
    </el-dialog>
  </div>
</template>

<script>
import { Alert, Empty, Switch } from 'element-ui'

export default {
  name: 'RobotQuickEducation',
  components: {
    ElAlert: Alert,
    ElEmpty: Empty,
    ElSwitch: Switch
  },
  data () {
    return {
      hospitals: [],
      departments: [],
      departmentsByHospital: {},
      hospitalName: '',
      departmentName: '',
      directories: [],
      selectedDirectoryId: '',
      categories: [],
      selectedCategoryId: '',
      articles: [],
      selectedArticles: [],
      selectedArticleIds: [],
      articleKeyword: '',
      searchTimer: null,
      articleDialogVisible: false,
      directoryDialogVisible: false,
      savingArticles: false,
      savingScope: false,
      useOtherDepartment: false,
      sourceHospitalName: '',
      sourceDepartmentName: '',
      sourceDepartments: [],
      directoryForm: {
        id: null,
        name: '',
        sortOrder: 0
      },
      directoryRules: { name: [{ required: true, message: '请输入目录名称', trigger: 'blur' }] }
    }
  },
  computed: {
    selectedDirectory () { return this.directories.find(item => String(item.id) === this.selectedDirectoryId) || null },
    filteredArticles () {
      const keyword = this.articleKeyword.trim().toLowerCase()
      return keyword ? this.articles.filter(item => (item.title || '').toLowerCase().includes(keyword)) : this.articles
    }
  },
  created () { this.loadDepartments() },
  methods: {
    async request (url, options) {
      const method = (options && options.method) || 'get'
      const config = (options && options.params) ? { params: options.params } : (options && options.config) || {}
      const response = method === 'get'
        ? await this.$http.get(url, config)
        : method === 'delete'
          ? await this.$http.delete(url, config)
          : method === 'post'
            ? await this.$http.post(url, options && options.data, config)
            : await this.$http.put(url, options && options.data, config)
      const resp = response.data
      if (resp.code !== 200) throw new Error(resp.message || '操作失败')
      return resp.data
    },
    async loadDepartments () {
      try {
        const data = await this.request('article/scope/hospital_departments')
        this.hospitals = (data.hospitals || []).filter(item => item.value)
        this.departmentsByHospital = data.departmentsByHospital || {}
      } catch (error) { this.$message.error(error.message) }
    },
    onHospitalChange () { this.departmentName = ''; this.directories = []; this.departments = (this.departmentsByHospital[this.hospitalName] || []).filter(item => item.value) },
    async loadConfig () {
      this.selectedDirectoryId = ''
      if (!this.hospitalName || !this.departmentName) return
      await this.loadContentScope()
      try {
        this.directories = await this.request('cms/robot-quick-education/directories', { params: { hospitalName: this.hospitalName, departmentName: this.departmentName } }) || []
        this.directories.forEach(item => { item.articles = [] })
        if (this.directories.length) {
          this.selectedDirectoryId = String(this.directories[0].id)
          await this.loadDirectoryArticles(this.directories[0])
        }
      } catch (error) { this.$message.error(error.message) }
    },
    async loadContentScope () {
      this.useOtherDepartment = false
      this.sourceHospitalName = ''
      this.sourceDepartmentName = ''
      this.sourceDepartments = []
      if (!this.hospitalName || !this.departmentName) return
      try {
        const data = await this.request('cms/robot-quick-education/content-scope', { params: { hospitalName: this.hospitalName, departmentName: this.departmentName } }) || {}
        this.useOtherDepartment = data.useOtherDepartment === true
        this.sourceHospitalName = data.sourceHospitalName || ''
        this.sourceDepartmentName = data.sourceDepartmentName || ''
        this.sourceDepartments = (this.departmentsByHospital[this.sourceHospitalName] || []).filter(item => item.value)
      } catch (error) { this.$message.error(error.message) }
    },
    onSourceHospitalChange () {
      this.sourceDepartmentName = ''
      this.sourceDepartments = (this.departmentsByHospital[this.sourceHospitalName] || []).filter(item => item.value)
    },
    /** 选好来源科室后自动开启并保存，无需再点开关。 */
    onSourceDepartmentChange () {
      if (!this.sourceDepartmentName) return
      this.useOtherDepartment = true
      this.saveContentScope()
    },
    onUseOtherChange (value) {
      if (!value) {
        this.saveContentScope(false)
      }
    },
    goToSourceDepartment () {
      if (!this.sourceHospitalName || !this.sourceDepartmentName) return
      this.hospitalName = this.sourceHospitalName
      this.onHospitalChange()
      this.departmentName = this.sourceDepartmentName
      this.loadConfig()
    },
    async saveContentScope (useOtherDepartment = this.useOtherDepartment) {
      if (this.savingScope) return
      this.savingScope = true
      try {
        const data = await this.request('cms/robot-quick-education/content-scope', {
          method: 'put',
          data: {
            hospitalName: this.hospitalName,
            departmentName: this.departmentName,
            useOtherDepartment,
            sourceHospitalName: this.sourceHospitalName,
            sourceDepartmentName: this.sourceDepartmentName
          }
        }) || {}
        this.useOtherDepartment = data.useOtherDepartment === true
        this.sourceHospitalName = data.sourceHospitalName || ''
        this.sourceDepartmentName = data.sourceDepartmentName || ''
        this.sourceDepartments = (this.departmentsByHospital[this.sourceHospitalName] || []).filter(item => item.value)
        this.$message.success(this.useOtherDepartment ? '已切换到所选医院科室的宣教内容' : '已恢复使用本科室宣教内容')
      } catch (error) {
        this.$message.error(error.message)
        await this.loadContentScope()
      } finally { this.savingScope = false }
    },
    async loadDirectoryArticles (directory) {
      try { this.$set(directory, 'articles', await this.request('cms/robot-quick-education/directories/' + directory.id + '/articles') || []) } catch (error) { this.$message.error(error.message) }
    },
    async selectDirectory (id) {
      this.selectedDirectoryId = id
      const directory = this.directories.find(item => String(item.id) === id)
      if (directory && !directory.articles.length) await this.loadDirectoryArticles(directory)
    },
    openDirectoryDialog (item) {
      this.directoryForm = item ? { id: item.id, name: item.name, sortOrder: item.sortOrder || 0 } : { id: null, name: '', sortOrder: this.directories.length }
      this.directoryDialogVisible = true
    },
    async saveDirectory () {
      try {
        await this.$refs.directoryForm.validate()
        await this.request('cms/robot-quick-education/directories', { method: 'post', data: { ...this.directoryForm, hospitalName: this.hospitalName, departmentName: this.departmentName } })
        this.directoryDialogVisible = false
        await this.loadConfig()
        this.$message.success('目录已保存')
      } catch (error) { if (error) this.$message.error(error.message || '请检查目录名称') }
    },
    deleteDirectory (item) {
      this.$confirm('删除目录“' + item.name + '”？目录内文章配置也会一并删除。', '确认删除', { type: 'warning' }).then(async () => {
        try { await this.request('cms/robot-quick-education/directories/' + item.id, { method: 'delete' }); await this.loadConfig(); this.$message.success('目录已删除') } catch (error) { this.$message.error(error.message) }
      }).catch(() => {})
    },
    async loadArticleCategories () {
      this.articleKeyword = ''; this.selectedCategoryId = ''; this.categories = []; this.articles = []; this.selectedArticles = []
      this.selectedArticleIds = ((this.selectedDirectory && this.selectedDirectory.articles) || []).map(item => item.id)
      try {
        const first = await this.request('category/find/first_category', { params: { type: 'a', role: 0, showInRobot: true } }) || []
        for (const group of first) {
          const children = await this.request('category/find/sec_category', { params: { firstId: group.firstId, role: 0 } }) || []
          children.forEach(item => this.categories.push({ id: item.id, firstName: group.firstName, secName: item.secName }))
        }
        if (this.categories.length) await this.selectCategory(String(this.categories[0].id))
      } catch (error) { this.$message.error(error.message) }
    },
    async selectCategory (id) {
      this.selectedArticles.forEach(item => { if (!this.selectedArticleIds.includes(item.id)) this.selectedArticleIds.push(item.id) })
      this.selectedCategoryId = id
      this.articleKeyword = ''
      try {
        const page = await this.request('cms/robot-quick-education/articles', { params: { categoryId: id, hospitalName: this.hospitalName, departmentName: this.departmentName } })
        this.showArticles((page || []).map(item => ({ ...item, scopeText: item.scopeText || '全局可见' })))
      } catch (error) { this.$message.error(error.message) }
    },
    searchArticles () {
      clearTimeout(this.searchTimer)
      const keyword = this.articleKeyword.trim()
      if (!keyword) {
        if (this.selectedCategoryId) this.selectCategory(this.selectedCategoryId)
        return
      }
      this.searchTimer = setTimeout(async () => {
        try {
          const result = await this.request('cms/robot-quick-education/articles/search', { params: { keyword, hospitalName: this.hospitalName, departmentName: this.departmentName } })
          this.showArticles((result || []).map(item => ({ ...item, scopeText: item.scopeText || '全局可见' })))
        } catch (error) { this.$message.error(error.message) }
      }, 300)
    },
    showArticles (articles) {
      this.articles = articles.sort((left, right) => {
        const leftCode = Number(left.displayCode) || Number.MAX_SAFE_INTEGER
        const rightCode = Number(right.displayCode) || Number.MAX_SAFE_INTEGER
        return leftCode - rightCode || left.id - right.id
      })
      this.selectedArticles = this.articles.filter(item => this.selectedArticleIds.includes(item.id))
      this.$nextTick(() => {
        const table = this.$refs.articleTable
        if (table) {
          table.clearSelection()
          this.selectedArticles.forEach(item => table.toggleRowSelection(item, true))
        }
      })
    },
    isArticleSelectable (row) { return true },
    onArticleSelection (rows) {
      this.articles.forEach(item => {
        const index = this.selectedArticleIds.indexOf(item.id)
        const selected = rows.some(row => row.id === item.id)
        if (selected && index < 0) this.selectedArticleIds.push(item.id)
        if (!selected && index >= 0) this.selectedArticleIds.splice(index, 1)
      })
      this.selectedArticles = rows
    },
    async saveArticles () {
      if (!this.selectedDirectory) return
      this.savingArticles = true
      try {
        this.selectedArticles.forEach(item => { if (!this.selectedArticleIds.includes(item.id)) this.selectedArticleIds.push(item.id) })
        const ids = Array.from(new Set(this.selectedArticleIds))
        await this.request('cms/robot-quick-education/directories/' + this.selectedDirectory.id + '/articles', { method: 'put', data: { articleIds: ids, hospitalName: this.hospitalName, departmentName: this.departmentName } })
        await this.loadDirectoryArticles(this.selectedDirectory)
        this.articleDialogVisible = false
        this.$message.success('文章配置已保存')
      } catch (error) { this.$message.error(error.message) } finally { this.savingArticles = false }
    },
    async removeArticle (article) {
      const ids = (this.selectedDirectory.articles || []).filter(item => item.id !== article.id).map(item => item.id)
      try { await this.request('cms/robot-quick-education/directories/' + this.selectedDirectory.id + '/articles', { method: 'put', data: { articleIds: ids, hospitalName: this.hospitalName, departmentName: this.departmentName } }); await this.loadDirectoryArticles(this.selectedDirectory) } catch (error) { this.$message.error(error.message) }
    }
  }
}
</script>

<style scoped>
.robot-quick-education { padding: 18px; }
.toolbar { margin: 20px 0 12px; }
.source-card { margin-top: 12px; }
.source-switch { margin-bottom: 12px; }
.source-body { display: flex; gap: 12px; align-items: center; }
.source-body .el-select { width: 260px; }
.source-tip { margin-top: 12px; color: #909399; font-size: 13px; line-height: 20px; }
.source-alert { margin-top: 12px; }
.source-link { color: #409eff; cursor: pointer; }
.workspace { margin-top: 16px; }
.panel-header { display: flex; align-items: center; justify-content: space-between; font-weight: 600; }
.directory-actions { float: right; color: #909399; }
.directory-actions i { padding: 0 6px; }
.article-picker { display: flex; gap: 16px; min-height: 440px; }
.category-list { width: 260px; overflow-y: auto; border-right: 1px solid #ebeef5; }
.article-results { flex: 1; min-width: 0; }
.keyword { margin-bottom: 12px; }
</style>
